<template>
    <section class="antialiased">
        <div class="mx-auto px-4 sm:px-6">
            <div class="mb-3 print:hidden opacity-50 hover:opacity-100 transition duration-300 ease-in-out" :class="{ 'opacity-100': comments.length }">
                <div class="mb-2 overflow-hidden rounded-lg bg-white shadow ">
                    <label for="comment" class="sr-only">Your comment</label>
                    <tiptap-editor
                        v-model="comment"
                        :users="users"
                        decorations
                        attachments
                        images
                        mentions
                        @toggle-attachments="toggleAttachments"
                        @get-users="getUsers"
                    >
                        <button
                            @click="postComment"
                            :disabled="postButtonDisabled"
                            type="submit"
                            :class="{
                                'bg-gray-50 text-gray-400 shadow-none': postButtonDisabled
                            }"
                            class="focus:outline-none inline-flex items-center rounded-md border border-gray-300 bg-white px-2 py-1.5 text-xs font-medium leading-4 text-gray-700 transition duration-150 ease-in-out hover:text-gray-500 focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-50 active:text-gray-800"
                        >
                            <svg
                                v-if="isLoading"
                                aria-hidden="true"
                                role="status"
                                class="mr-2 inline h-4 w-4 animate-spin text-gray-200 "
                                viewBox="0 0 100 101"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                    fill="currentColor"
                                ></path>
                                <path
                                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                    fill="#1C64F2"
                                ></path>
                            </svg>
                            {{ isLoading ? 'Saving' : 'Post' }}
                        </button>
                    </tiptap-editor>
                    <uploadcare-inline
                        v-show="showAttachments"
                        :key="`comment_attachment_${uploadcareKey}`"
                        :ctxName="`comment_attachment_${uploadcareKey}`"
                        inputAcceptTypes="image/*, .pdf"
                        multiple
                        :multipleMax="5"
                        tabs="local, dropbox, url, gdrive"
                        @success="onImageSuccess"
                    />
                </div>
                <p class="text-right text-xs text-gray-500"
                    >Only you and other staff can see comments
                </p>
            </div>
            <div
                class="comment-list relative z-50"
                v-auto-animate
                v-if="!orderId"
            >
                <div
                    v-for="comment of comments"
                    :key="comment.id"
                    :id="`comment-${comment.id}`"
                >
                    <comment-component
                        :comment="comment"
                        @fetch-comments="fetchComments"
                    />
                </div>
            </div>
        </div>
    </section>
</template>

<script>
import TiptapEditor from '@/components/comments/TiptapEditor';
import CommentComponent from '@/components/comments/CommentComponent';
import UploadcareInline from '@/components/widgets/UploadcareInline';

export default {
    name: 'CommentSection',
    props: {
        comments: {
            type: Array,
            default: () => []
        },
        orderId: {
            type: String,
            default: null
        },
        discountId: {
            type: String,
            default: null
        },
        users: {
          type: Array,
          default: () => []
        }
    },
    components: {
        TiptapEditor,
        CommentComponent,
        UploadcareInline
    },
    data() {
        return {
            comment: '',
            isLoading: false,
            attachments: [],
            uploadcareKey: 1,
            showAttachments: false
        };
    },
    computed: {
        postButtonDisabled() {
            return (
                this.isLoading || (!this.comment && !this.attachments.length)
            );
        }
    },
    methods: {
        async postComment() {
            if (this.postButtonDisabled) {
                return;
            }

            this.isLoading = true;

            const payload = {
              text: this.comment,
              attachments: this.attachments
            }

            if (this.orderId) {
              payload.orderId = this.orderId
            }

            if (this.discountId) {
              payload.discountCodeId = this.discountId
            }

            try {
                await this.$axios.post(`/comments`, payload);

                this.uploadcareKey++;
                this.attachments = [];
                this.comment = '';
                this.fetchComments();
                this.showAttachments = false;
            } catch (error) {
                throw new Error(`API ${error}`);
            } finally {
                this.isLoading = false;
            }
        },

        fetchComments() {
            this.$emit('fetch-comments');
        },
        async getUsers() {
            try {
                const { data } = await this.$axios.get(`/users/list-names`);
                this.users = data;
            } catch (error) {
                throw new Error(`API ${error}`);
            }
        },
        onImageSuccess(event) {
            if (Array.isArray(event)) {
                this.attachments = event.map(
                    ({ cdnUrl, originalFilename }) => ({
                        url: cdnUrl,
                        name: originalFilename
                    })
                );
            } else {
                this.attachments = [
                    {
                        name: event.originalFilename,
                        url: event.cdnUrl
                    }
                ];
            }
        },
        toggleAttachments() {
            this.showAttachments = !this.showAttachments;
        }
    }
};
</script>

<style>
.comment-link {
    @apply text-blue-600 hover:underline;
}

.comment-list:after {
    width: 2px;
    background: #e3e3e3;
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    z-index: -1;
    left: 40px;
}
</style>
